import { useEffect, useCallback } from 'react'
import { getImageUrl } from '../../../utils/helpers'
import type { UnifiedGalleryItem } from '../Gallery'

interface LightboxProps {
  item: UnifiedGalleryItem
  onClose: () => void
}

function Lightbox({ item, onClose }: LightboxProps) {
  // Handle escape key and body scroll
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    },
    [onClose]
  )

  useEffect(() => {
    // Add event listener for escape key
    document.addEventListener('keydown', handleKeyDown)

    // Prevent body scroll when lightbox is open
    document.body.style.overflow = 'hidden'

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = ''
    }
  }, [handleKeyDown])

  // Handle click outside (on overlay)
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  // Render the appropriate media content
  const renderMedia = () => {
    if (item.type === 'youtube') {
      // YouTube video - render iframe
      return (
        <div className="lightbox-youtube">
          <iframe
            src={item.embed_url}
            title={item.title}
            width="100%"
            height="100%"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        </div>
      )
    }

    // Uploaded media
    const mediaUrl = item.file_path ? getImageUrl(item.file_path) : item.thumbnail_url

    if (item.is_video) {
      return (
        <video src={mediaUrl} controls autoPlay playsInline>
          Votre navigateur ne supporte pas la lecture video.
        </video>
      )
    }

    return <img src={mediaUrl} alt={item.title} />
  }

  return (
    <div className="lightbox-overlay" onClick={handleOverlayClick}>
      <button className="lightbox-close" onClick={onClose} aria-label="Fermer">
        x
      </button>

      <div className="lightbox-content">
        <div className={`lightbox-media ${item.type === 'youtube' ? 'lightbox-media-youtube' : ''}`}>
          {renderMedia()}
        </div>

        <div className="lightbox-info">
          <h3>{item.title}</h3>
          {item.description && item.description.trim() !== '' && (
            <p>{item.description}</p>
          )}
        </div>
      </div>
    </div>
  )
}

export default Lightbox
